import { useCallback, useEffect, useRef, useState } from "react";
import type { UseFormGetValues, UseFormReset, UseFormTrigger, UseFormWatch } from "react-hook-form";
import { toast } from "sonner";
import type { Site } from "@/bryntum-lib/models/Site";
import type { ISwivuProject } from "@/types/SwivuProject";
import { applySiteFormData, siteToFormData, type SiteFormData } from "@/react/schema/siteSchema";
import { isSiteLive } from "../utils";
import { confirmSlugRepublishIfNeeded } from "./useSlugRepublishConfirm";

const AUTO_SAVE_DELAY_MS = 600;

type UseSiteFormAutoSaveParams = {
  activeSite: Site | null;
  project: ISwivuProject | null | undefined;
  siteReady: boolean;
  isReadOnly: boolean;
  isBusy: boolean;
  isEnsuring: boolean;
  isDirty: boolean;
  getValues: UseFormGetValues<SiteFormData>;
  reset: UseFormReset<SiteFormData>;
  trigger: UseFormTrigger<SiteFormData>;
  watch: UseFormWatch<SiteFormData>;
};

export function useSiteFormAutoSave({
  activeSite,
  project,
  siteReady,
  isReadOnly,
  isBusy,
  isEnsuring,
  isDirty,
  getValues,
  reset,
  trigger,
  watch,
}: UseSiteFormAutoSaveParams) {
  const [isSaving, setIsSaving] = useState(false);
  const saveInFlightRef = useRef(false);
  const slugConfirmDismissedForRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const guardsRef = useRef({ isReadOnly, isBusy, isEnsuring, siteReady });
  guardsRef.current = { isReadOnly, isBusy, isEnsuring, siteReady };

  const persistSiteChanges = useCallback(async (): Promise<boolean> => {
    const { isReadOnly: readOnly, isBusy: busy, isEnsuring: ensuring, siteReady: ready } = guardsRef.current;
    if (!activeSite || !project || readOnly || !ready || ensuring || busy) {
      return false;
    }

    const valid = await trigger();
    if (!valid) return false;

    const values = getValues();
    const slugDirty = values.slug !== (activeSite.slug ?? "");
    let dataToApply = values;

    if (isSiteLive(activeSite) && slugDirty) {
      if (slugConfirmDismissedForRef.current === values.slug) {
        dataToApply = { ...values, slug: activeSite.slug ?? "" };
      } else {
        const confirmed = await confirmSlugRepublishIfNeeded(true, true);
        if (!confirmed) {
          slugConfirmDismissedForRef.current = values.slug;
          dataToApply = { ...values, slug: activeSite.slug ?? "" };
        } else {
          slugConfirmDismissedForRef.current = null;
        }
      }
    }

    applySiteFormData(activeSite, dataToApply);
    try {
      await project.sync();
      const synced = siteToFormData(activeSite);
      reset(slugDirty && dataToApply.slug !== values.slug ? { ...synced, slug: values.slug } : synced);
      return true;
    } catch {
      toast.error("Could not save site changes.");
      return false;
    }
  }, [activeSite, project, trigger, getValues, reset]);

  const persistRef = useRef(persistSiteChanges);
  persistRef.current = persistSiteChanges;

  const runSave = useCallback(async () => {
    if (saveInFlightRef.current) return;
    saveInFlightRef.current = true;
    setIsSaving(true);
    try {
      await persistRef.current();
    } finally {
      saveInFlightRef.current = false;
      setIsSaving(false);
    }
  }, []);

  const scheduleSave = useCallback(() => {
    const { isReadOnly: readOnly, isBusy: busy, isEnsuring: ensuring, siteReady: ready } = guardsRef.current;
    if (readOnly || !ready || ensuring || busy) return;

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      void runSave();
    }, AUTO_SAVE_DELAY_MS);
  }, [runSave]);

  useEffect(() => {
    if (!activeSite || !siteReady) return;

    const subscription = watch((_, info) => {
      if (info?.type === "change") {
        scheduleSave();
      }
    });

    return () => {
      subscription.unsubscribe();
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [activeSite, siteReady, watch, scheduleSave]);

  useEffect(() => {
    if (isDirty) scheduleSave();
  }, [isDirty, scheduleSave]);

  useEffect(() => {
    slugConfirmDismissedForRef.current = null;
  }, [activeSite?.id]);

  const flushSave = useCallback(async (): Promise<boolean> => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    while (saveInFlightRef.current) {
      await new Promise((resolve) => setTimeout(resolve, 50));
    }

    if (!activeSite) return false;

    const values = getValues();
    const synced = siteToFormData(activeSite);
    const hasPendingChanges = (Object.keys(synced) as (keyof SiteFormData)[]).some(
      (key) => values[key] !== synced[key],
    );
    if (!hasPendingChanges) return true;

    return persistRef.current();
  }, [activeSite, getValues]);

  return { isSaving, flushSave };
}
